import React, { useEffect, useRef, useState } from "react";

/**
 * The Ring — one mark doing five jobs: app icon, voice orb, loading state,
 * avatar and memory motif. A sound ripple and a tree ring at once.
 *
 * states: idle | listening | thinking | speaking | muted | error
 * Colour is never the only signal — every state ships a text label.
 */

const LABELS = {
  idle: "",
  listening: "Listening",
  thinking: "Thinking",
  speaking: "Speaking",
  muted: "Microphone off",
  error: "Something went wrong",
};

const LIVE_STATES = new Set(["listening", "thinking", "speaking"]);

export const Ring = ({
  state = "idle",
  size = 96,
  amplitude = 0,
  showLabel = true,
  className = "",
  onClick,
  ariaLabel,
  testId = "ring",
}) => {
  // Low-pass the incoming RMS (~120ms) so the ring reads as alive, not broken.
  const [damped, setDamped] = useState(0);
  const target = useRef(0);
  const current = useRef(0);

  useEffect(() => {
    target.current = Math.max(0, Math.min(1, amplitude || 0));
  }, [amplitude]);

  useEffect(() => {
    if (state !== "listening") {
      current.current = 0;
      setDamped(0);
      return;
    }
    let raf;
    const tick = () => {
      // ~120ms time constant at 60fps
      current.current += (target.current - current.current) * 0.13;
      setDamped(current.current);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state]);

  const live = LIVE_STATES.has(state);
  const label = LABELS[state] || "";
  const Tag = onClick ? "button" : "div";

  // Geometry — core 14, rings at 1 : 1.7 : 2.4 radius ratio.
  const rMid = 23.8;
  const rOuter = 33.6 + damped * 9;
  const tiny = size < 32;

  return (
    <div
      className={`inline-flex flex-col items-center gap-2 ${className}`}
      data-testid={testId}
      data-state={state}
    >
      <Tag
        type={onClick ? "button" : undefined}
        onClick={onClick}
        aria-label={ariaLabel || label || "Rammy"}
        data-testid={onClick ? `${testId}-button` : undefined}
        style={{ width: size, height: size }}
        className={`relative grid place-items-center rounded-full ${
          onClick ? "transition-transform active:scale-[0.98] focus-visible:outline-none" : ""
        }`}
      >
        {/* Live glow — teal, and only while the voice machine is running. */}
        {live && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{ boxShadow: "var(--rm-shadow-live)" }}
          />
        )}

        <svg
          viewBox="0 0 100 100"
          width={size}
          height={size}
          aria-hidden="true"
          className={`rm-ring-anim ${
            state === "idle" ? "animate-[rm-ring-breathe_2600ms_ease-in-out_infinite]" : ""
          } ${state === "error" ? "animate-[rm-ring-shake_180ms_ease-in-out_2]" : ""}`}
        >
          {/* Speaking — rings emit outward from the core. */}
          {state === "speaking" && (
            <g>
              {[0, 300, 600].map((delay) => (
                <circle
                  key={delay}
                  cx="50"
                  cy="50"
                  r="34"
                  fill="none"
                  stroke="var(--rm-live)"
                  strokeWidth="1.6"
                  style={{
                    transformOrigin: "50px 50px",
                    animation: `rm-ring-ripple 900ms var(--rm-ease-out) ${delay}ms infinite`,
                  }}
                />
              ))}
            </g>
          )}

          {/* Outer ring */}
          <circle
            cx="50"
            cy="50"
            r={state === "listening" ? rOuter : 33.6}
            fill="none"
            stroke={
              state === "error"
                ? "var(--rm-danger)"
                : state === "muted"
                ? "var(--rm-ink-300)"
                : state === "listening"
                ? "var(--rm-live)"
                : "var(--rm-indigo-200)"
            }
            strokeWidth="1.5"
            style={{ transition: state === "listening" ? "none" : "r 220ms var(--rm-ease-out)" }}
          />

          {/* Middle ring — carries the thinking dash. */}
          {state !== "error" && (
            <circle
              cx="50"
              cy="50"
              r={rMid}
              fill="none"
              stroke={
                state === "muted"
                  ? "var(--rm-ink-300)"
                  : state === "thinking"
                  ? "var(--rm-live)"
                  : "var(--rm-indigo-300)"
              }
              strokeWidth="1.8"
              strokeDasharray={state === "thinking" ? "22 42" : undefined}
              strokeLinecap={state === "thinking" ? "round" : undefined}
              style={
                state === "thinking"
                  ? {
                      transformOrigin: "50px 50px",
                      animation: "rm-ring-spin 1400ms linear infinite",
                    }
                  : undefined
              }
            />
          )}

          {/* Core — always indigo. It never changes colour with state. */}
          <circle
            cx="50"
            cy="50"
            r="14"
            fill={state === "muted" ? "var(--rm-ink-400)" : "var(--rm-brand)"}
          />
          <circle cx="50" cy="50" r="5.2" fill="var(--rm-paper-0)" />

          {/* Muted — slash through the core. */}
          {state === "muted" && (
            <line
              x1="34"
              y1="66"
              x2="66"
              y2="34"
              stroke="var(--rm-ink-500)"
              strokeWidth="3"
              strokeLinecap="round"
            />
          )}
        </svg>
      </Tag>

      {/* Colour is never the sole carrier of meaning. */}
      {showLabel && !tiny && (
        <span
          className="rm-ring-label font-mono uppercase"
          data-testid={`${testId}-label`}
          style={{
            fontSize: "var(--rm-text-2xs)",
            letterSpacing: "var(--rm-tracking-label)",
            color:
              state === "error"
                ? "var(--rm-danger)"
                : live
                ? "var(--rm-live-ink)"
                : "var(--rm-text-faint)",
            minHeight: 14,
          }}
        >
          {label}
        </span>
      )}

      <span aria-live="assertive" className="sr-only">
        {label}
      </span>
    </div>
  );
};

export default Ring;
